import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { WEB_SERVICE_MIMS, protocols, applications } from "../../../config/constants";
import { handleSetProtocolsCategory } from "../../../config/functions";
import { RenderProtocols } from "../sections/renderProtocols";
// Components
import { Form } from "./styles";
import Axios from "../../../config/axios";
import Loader from "../../loader";
import PatientNavigation from "../../patientNavigation";
import Title from "../../title";
import ActionsManagement from "./sections/actionsManagement";
import ProtocolInfo from "../sections/protocolInfo";
import ProtocolHeader from "../sections/protocolHeader";
import CheckChangesWithoutSaving from "../../checkChangesWithoutSaving";

const PatientPrescriptions = ({ software }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabledInputs, setIsDisabledInputs] = useState(true);
  const [isRechargeComponent, setIsRechargeComponent] = useState(false);
  const [hospitalProtocols, setHospitalProtocols] = useState([]);
  // Prescriptions
  const [towersPrescriptions, setTowersPrescriptions] = useState([]);
  const [balloonsPrescriptions, setBalloonsPrescriptions] = useState([]);
  const [calibrationPrescriptions, setCalibrationPrescriptions] = useState([]);
  const [runnerPrescriptions, setRunnerPrescriptions] = useState([]);
  const [dataCollectionPrescriptions, setDataCollectionPrescriptions] = useState([]);
  const [practicePrescriptions, setPracticePrescriptions] = useState([]);
  const [prescriptionsToDatabase] = useState([]);
  const { patientId } = useParams();
  const hospitalId = useSelector(({ clinician }) => clinician.data.HOSPITAL_ID);
  const [t] = useTranslation("global");

  const handleSetPrescriptions = (prescriptions, protocolId, setProtocolsPrescription) => {
    const filteredPrescriptions = prescriptions.filter((prescription) => {
      return prescription.PROTOCOL_ID === protocolId;
    });
    setProtocolsPrescription(filteredPrescriptions);
  };

  // Set protocol prescriptions
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      Axios("get", `${WEB_SERVICE_MIMS}/patients/prescriptions/get/${patientId}/${software}`)
        .then(({ data }) => {
          handleSetPrescriptions(data, protocols.clinicAndHome.TOWERS.id, setTowersPrescriptions);
          handleSetPrescriptions(data, protocols.clinicAndHome.BALLOONS.id, setBalloonsPrescriptions);
          handleSetPrescriptions(data, protocols.clinicAndHome.CALIBRATION.id, setCalibrationPrescriptions);
          handleSetPrescriptions(data, protocols.clinicAndHome.RUNNER.id, setRunnerPrescriptions);
          handleSetPrescriptions(data, protocols.clinicAndHome.DATA_COLLECTION.id, setDataCollectionPrescriptions);
          handleSetPrescriptions(data, protocols.clinicAndHome.PRACTICE.id, setPracticePrescriptions);
          setIsLoading(false);
        })
        .catch((error) => {
          console.log("url error: ", error);
        });
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, software, isRechargeComponent]);

  // Fetching hospital protocols
  useEffect(() => {
    let softwareId;
    switch (software) {
      case "clinic":
        softwareId = applications.clinicId;
        break;
      case "home":
        softwareId = applications.homeId;
        break;
      default:
        break;
    }
    Axios("get", `${WEB_SERVICE_MIMS}/hospitals/protocols/${softwareId}/${hospitalId}`)
      .then(({ data }) => {
        const formattedHospitalProtocols = [];
        data.forEach((protocol) => {
          formattedHospitalProtocols.push(protocol.PROTOCOL_ID);
        });
        setHospitalProtocols(formattedHospitalProtocols);
      })
      .catch((error) => {
        console.log("endpoint to get hospital protocols: ", error);
      });
  }, [software, hospitalId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const URL = `${WEB_SERVICE_MIMS}/patients/prescriptions/add/${patientId}/${software}`;
    if (prescriptionsToDatabase.length > 0) {
      Axios("post", URL, prescriptionsToDatabase)
        .then(({ data }) => {
          if (data.success) {
            toast.success(t("prescriptions.subscriptionAddedMessage"));
            setIsDisabledInputs(true);
            setIsRechargeComponent(!isRechargeComponent);
          } else {
            toast.error(t("profileMessage.sendDataMessage"));
          }
        })
        .catch((error) => {
          toast.error(t("profileMessage.sendDataMessage"));
          console.log("endpoint to add patient: ", error);
        });
    } else {
      setIsDisabledInputs(true);
    }
  };

  const handleRenderProtocols = () => {
    let orderedClinicAndHomeProtocols = [];
    const clinicAndHomeProtocols = Object.values(protocols.clinicAndHome);
    handleSetProtocolsCategory(clinicAndHomeProtocols, orderedClinicAndHomeProtocols, hospitalProtocols);

    return orderedClinicAndHomeProtocols.map((protocolId) => {
      let protocolPrescriptions;
      switch (Number(protocolId)) {
        case protocols.clinicAndHome.TOWERS.id:
          protocolPrescriptions = towersPrescriptions;
          break;
        case protocols.clinicAndHome.BALLOONS.id:
          protocolPrescriptions = balloonsPrescriptions;
          break;
        case protocols.clinicAndHome.CALIBRATION.id:
          protocolPrescriptions = calibrationPrescriptions;
          break;
        case protocols.clinicAndHome.RUNNER.id:
          protocolPrescriptions = runnerPrescriptions;
          break;
        case protocols.clinicAndHome.DATA_COLLECTION.id:
          protocolPrescriptions = dataCollectionPrescriptions;
          break;
        case protocols.clinicAndHome.PRACTICE.id:
          protocolPrescriptions = practicePrescriptions;
          break;
        default:
          break;
      }
      return RenderProtocols(protocolId, t, protocolPrescriptions, isDisabledInputs, prescriptionsToDatabase, patientId, software);
    });
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={software} />
          <Title title="iLimbApp" subtitle={`/ ${t("prescriptions.title")}`} marginBottom="10px" />
          <ProtocolInfo />
          <ActionsManagement
            handleSubmit={handleSubmit}
            isDisabledInputs={isDisabledInputs}
            setIsDisabledInputs={setIsDisabledInputs}
            prescriptionsToDatabase={prescriptionsToDatabase}
          />
          <ProtocolHeader section={software} />
          <Form onSubmit={handleSubmit}>{handleRenderProtocols()}</Form>
        </>
      )}
      <CheckChangesWithoutSaving isDisabledInputs={isDisabledInputs} />
    </>
  );
};

export default PatientPrescriptions;
